import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, ViewChild, ElementRef, Input, Renderer2, HostListener, OnDestroy, OnChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { EventEmitterService } from '../../../../Services/Common/event-emitter.service';
import { CookieService } from 'ngx-cookie-service';
import { SecureStorageService } from '../../../../Services/cache';
import { getEffectiveViewportWidth, getZoomPresetForWidth } from '../../../../core/utils/viewport.util';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { shortcuts } from '../../../../Interfaces/content.interface';
import jsonData from '../../../../../assets/JSONData/keyboardshortcuts.json'
import { readEntirePage, resetHeaderSpokenState } from '../../../../core/utils/speech-utils';
import { OsDetectionService, ThemeService } from '../../../../Services/platform';
import { AuthService } from '../../../../Services/auth';

@Component({
  selector: 'app-accessibilty',
  imports: [MatSlideToggleModule, MatDialogModule, CommonModule, FormsModule, MatIconModule],
  templateUrl: './accessibilty.component.html',
  styleUrl: './accessibilty.component.scss'
})
export class AccessibiltyComponent implements OnInit, OnDestroy {

  @Output() closeAccessibility: EventEmitter<any> = new EventEmitter();
  @Input() isDark: boolean = false;
  @Input() isHighContrastCheck: boolean = false;
  activeBtn = 'normal'
  // fontStepIndex: 0..6 where 3 is normal, <3 smaller, >3 larger
  fontStepIndex: number = 3;
  readyToListen = false;
  darkModeEnabled = false;
  highContrastEnabled = false;
  currentTheme: 'light' | 'dark' = 'light';
  isHighContrast: boolean = false;
  isShow = true
  deviceInfo: any;
  ctrlButton: string = '';
  listenShortcut: string = 'Insert + H';

  darkModeShortCut: string = '';
  highContrastShortCut: string = '';
  keyboardData: shortcuts
  hoverListener: (() => void) | null = null;
  authSub!: Subscription;
  private themeSub!: Subscription;
  private highContrastSub!: Subscription;
  isLoggedIn: boolean = false;
  private busSubscription: Subscription | null = null;


  private keydownListener!: (event: KeyboardEvent) => void

  constructor(private router: Router, private themeService: ThemeService, private dialog: MatDialog, private renderer: Renderer2, private device: DeviceDetectorService, private cookieService: CookieService, private secureStorage: SecureStorageService, private osService: OsDetectionService, private sso: ScrollStrategyOptions, private authService: AuthService, private bus: EventEmitterService) {
    this.keyboardData = this.getKeyboardDataForOs(jsonData["shortcuts"]);
  }

  private getKeyboardDataForOs(data: shortcuts): shortcuts {
    if (!this.osService.isMac()) return data;

    const transformed = { ...data } as shortcuts;
    transformed.tableData = data.tableData.map(row => {
      const newRow = [...row];
      if (typeof newRow[1] === 'string') {
        let s = newRow[1] as string;
        s = s.replace(/Ctrl/g, 'Cmd');
        s = s.replace(/Alt/g, 'Cmd + Opt');
        s = s.replace(/Insert\s*\+\s*H/i, 'Cmd + Ctrl + H');
        newRow[1] = s;
      }
      return newRow;
    });
    // Also update the explicit listenShortcut label used in UI
    this.listenShortcut = 'Cmd + Ctrl + H';
    return transformed;
  }

  ngOnInit(): void {
    this.authSub = this.authService.loggedIn$.subscribe((v: boolean) => this.isLoggedIn = !!v);
    this.readyToListen = this.isSpeechActive();
    const storedListenState = localStorage.getItem('accessibility-listen');
    const stored = storedListenState === 'true';
    const isSpeaking = typeof window !== 'undefined' && 'speechSynthesis' in window && window.speechSynthesis.speaking;
    if (stored && !isSpeaking) {
      localStorage.setItem('accessibility-listen', 'false');
      this.readyToListen = false;
    } else {
      this.readyToListen = stored;
    }
    // this.syncTextSizeWithFont();
    this.syncZoomButton();

    // Subscribe to theme service so toggles reflect persisted state
    this.themeSub = this.themeService.theme$.subscribe(theme => {
      this.currentTheme = theme;
      this.darkModeEnabled = theme === 'dark';
    });

    this.highContrastSub = this.themeService.isHighContrast$.subscribe(isHighContrast => {
      this.isHighContrast = isHighContrast;
      this.highContrastEnabled = isHighContrast;
    });

    // Ensure UI matches current theme state
    this.syncThemeStates();

    this.deviceInfo = this.device.getDeviceInfo();
    console.log("device info in accessibility", this.deviceInfo);
    this.ctrlButton = this.deviceInfo.os.toLocaleLowerCase() === 'mac' ? 'Cmd' : 'Ctrl';

    if (this.deviceInfo.os.toLocaleLowerCase() === 'mac') {
      this.darkModeShortCut = 'Cmd + Opt + 1'
      this.highContrastShortCut = 'Cmd + Opt + 2'
    } else {

    }

    this.keydownListener = this.handleKeyboardEvent.bind(this);
    document.addEventListener('keydown', this.keydownListener, { capture: true, passive: false });
    // use Angular HostListener for keydown handling (no manual addEventListener)
    // listen for external toggles (header keyboard shortcut)
    this.busSubscription = this.bus.on().subscribe((ev: string) => {
      if (ev === 'accessibility-listen-toggled') {
        const stored = localStorage.getItem('accessibility-listen') === 'true';
        this.readyToListen = stored;
      }
    });
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.keydownListener, { capture: true });
    if (this.authSub) this.authSub.unsubscribe();
    if (this.themeSub) this.themeSub.unsubscribe();
    if (this.highContrastSub) this.highContrastSub.unsubscribe();
    // No manual listener to remove; HostListener handles cleanup automatically

    // Only clean up speech if it's not supposed to persist
    const storedListenState = localStorage.getItem('accessibility-listen');
    const shouldKeepListening = storedListenState === 'true';

    if (!shouldKeepListening) {
      window.speechSynthesis.cancel();
      if (this.hoverListener) {
        this.hoverListener();
        this.hoverListener = null;
      }
    }
    if (this.busSubscription) {
      try { this.busSubscription.unsubscribe(); } catch (e) { }
      this.busSubscription = null;
    }
  }

  isSpeechActive(): boolean {
    return speechSynthesis.speaking && !speechSynthesis.paused;
  }

  closeAccess() {
    this.closeAccessibility.emit();
    //this.readyToListen = this.isSpeechActive();
    this.syncTextSizeWithFont();
    if (!this.readyToListen) {
      window.speechSynthesis.cancel();
      if (this.hoverListener) {
        this.hoverListener();
        this.hoverListener = null;
      }
      localStorage.setItem('accessibility-listen', 'false');
    }
    this.syncThemeStates();
    // const accessHeader = document.querySelector('.access-header') as HTMLElement;
    // if (accessHeader) {
    //   accessHeader.classList.remove('active-access');
    // }
    this.syncThemeStates();
    this.syncZoomButton()
  }

  @Output() fontSizeChanged = new EventEmitter<void>();

  private getCurrentZoomPreset() {
    const effectiveWidth = getEffectiveViewportWidth();
    return getZoomPresetForWidth(effectiveWidth);
  }

  //  private computeZoomSteps(zoomOut: number, normal: number, zoomIn: number): number[] {
  //     const steps: number[] = [];
  //     const low = zoomOut;
  //     const high = zoomIn;
  //     for (let i = 0; i < 7; i++) {
  //       const t = i / 6; // 0..1
  //       const value = low + (high - low) * t;
  //       steps.push(Number(value.toFixed(4)));
  //     }
  //     steps[3] = normal;
  //     return steps;
  //   }

  private computeZoomSteps(zoomOut: number, normal: number, zoomIn: number): number[] {
    const steps: number[] = [];
    // Generate 7 steps with index 3 forced to `normal` while keeping monotonic order.
    // Produce 4 values between zoomOut -> normal (inclusive), and 3 values between normal -> zoomIn (exclusive of normal).
    // Left side: indices 0..3 (4 points)
    for (let i = 0; i <= 3; i++) {
      const t = i / 3; // 0..1
      const value = zoomOut + (normal - zoomOut) * t;
      steps.push(Number(value.toFixed(4)));
    }
    // Right side: indices 4..6 (3 points)
    for (let j = 1; j <= 3; j++) {
      const t = j / 3; // 1/3..1
      const value = normal + (zoomIn - normal) * t;
      steps.push(Number(value.toFixed(4)));
    }

    return steps;
  }
  // setTextSize(type: string) {
  //   this.activeBtn = type;
  //   const wrapper = document.getElementById('zoom-wrapper');
  //   const accessHeader = document.querySelector('.access-header') as HTMLElement

  //   if (!wrapper || !accessHeader) return;

  //   let zoom = '1';
  //   let fontWeight = 'normal';
  //   let borderBottom = '1px solid';

  //   // console.log("access Header", accessHeader)

  //   switch (type) {
  //     case 'zoomIn':
  //       zoom = window.innerWidth < 1440 && window.innerWidth > 1100 ? '0.8' :
  //         window.innerWidth > 1500 ? '1.20' : '1.05';
  //       fontWeight = 'bold'
  //       borderBottom = '3px solid #FFB937'
  //       break;
  //     case 'zoomOut':
  //       zoom = window.innerWidth < 1440 && window.innerWidth > 1100 ? '0.7' :
  //         window.innerWidth > 1500 ? '1.10' : '0.95';
  //       fontWeight = 'normal'
  //       borderBottom = '1px solid #FFB937'
  //       break;
  //     default:
  //       zoom = window.innerWidth < 1440 && window.innerWidth > 1100 ? '0.75' :
  //         window.innerWidth > 1500 ? '1.15' : '1';
  //       fontWeight = '500'
  //       borderBottom = '2px solid #FFB937'
  //       break;
  //   }

  //   wrapper.style.zoom = zoom;
  //   this.cookieService.set("zoom-level", zoom);


  //   accessHeader.style.fontWeight = fontWeight;
  //   accessHeader.style.borderBottom = borderBottom;

  //   this.fontSizeChanged.emit();
  //   // console.log('Font size changed:', type);
  // }

  // setTextSize(type: string) {
  //   this.activeBtn = type

  //   switch (type) {
  //     case 'zoomIn':
  //       wrapper.style.zoom = '1.05'; // 105%
  //       break;
  //     case 'zoomOut':
  //       wrapper.style.zoom = '0.95'; // 95%
  //       break;
  //     default:
  //       // Set zoom based on screen width
  //       if (window.innerWidth < 1380) {
  //         wrapper.style.zoom = '0.9';
  //       } else {
  //         wrapper.style.zoom = '1';
  //       }
  //       break;
  //   }

  //   this.fontSizeChanged.emit();
  //   console.log('Font size changed:', type);
  // }


  setTextSize(type: string) {
    this.activeBtn = type;
    const wrapper = document.getElementById('zoom-wrapper');
    if (!wrapper) return;

    const preset = this.getCurrentZoomPreset();
    const steps = this.computeZoomSteps(preset.zoomOut, preset.normal, preset.zoomIn);

    if (type === 'zoomIn') {
      if (this.fontStepIndex < steps.length - 1) this.fontStepIndex++;
    } else if (type === 'zoomOut') {
      if (this.fontStepIndex > 0) this.fontStepIndex--;
    } else {
      this.fontStepIndex = 3; // normal
    }

    const zoomValue = steps[this.fontStepIndex];
    wrapper.style.zoom = zoomValue.toString();
    document.documentElement.style.setProperty('--app-zoom', zoomValue.toString());
    this.secureStorage.set('font-step-index', this.fontStepIndex.toString(), true);
    this.activeBtn = type;
    this.secureStorage.set('active-btn', this.activeBtn, true);

    this.fontSizeChanged.emit();
    // console.log('Font size changed:', type);
  }

  syncZoomButton() {
    const stepStr = this.secureStorage.get<string>('font-step-index', true);
    this.fontStepIndex = stepStr ? parseInt(stepStr, 10) : 3;
    
    const preset = this.getCurrentZoomPreset();
    const steps = this.computeZoomSteps(preset.zoomOut, preset.normal, preset.zoomIn);
    const zoomValue = steps[this.fontStepIndex];

    const wrapper = document.getElementById('zoom-wrapper');
    if (wrapper) {
      wrapper.style.zoom = zoomValue.toString();
    }
    document.documentElement.style.setProperty('--app-zoom', zoomValue.toString());

    this.activeBtn = this.secureStorage.get<string>('active-btn', true) || 'normal';
  }

  reset() {
    try {
      this.secureStorage.remove('font-step-index', true);
      this.secureStorage.remove('active-btn', true);
      this.secureStorage.remove('zoom-level', true);
      this.cookieService.delete('zoom-level');
      this.cookieService.delete('zoom-level', '/');
    } catch (error) {
      console.debug('Unable to clear font session state', error);
    }

    this.activeBtn = 'normal';
    this.darkModeEnabled = false;
    this.highContrastEnabled = false;
    localStorage.setItem('theme', 'light');
    localStorage.setItem('high-contrast', 'false');
    this.isHighContrast = false;
    this.currentTheme = 'light';
    this.themeService.toggleReset();
    this.setAnnouncementBackgroundSize();

    const wrapper = document.getElementById('zoom-wrapper');
    if (!wrapper) return;

    const preset = this.getCurrentZoomPreset();
    wrapper.style.zoom = preset.normal.toString();
    document.documentElement.style.setProperty('--app-zoom', preset.normal.toString());
    this.fontStepIndex = 3;

    // Remove font-step-index from sessionStorage
    sessionStorage.removeItem("font-step-index");

    this.fontSizeChanged.emit();
    try {
      this.disableSpeech();
    } catch (e) {
      console.error('Failed to disable speech during reset', e);
    }
  }

  // listenToPage() {
  //   this.readyToListen = !this.readyToListen

  //   if (this.readyToListen) {
  //     console.log('entered listen to page');

  //     //     let textAreaContent = 'Welcome to One Tax Bahamas. The tax system was introduced in The Bahamas in January 2015 as part of a broader reform of the tax system. The broad based consumption tax is applied to almost all goods and services that are imported, bought and sold for use in The Bahamas. Two rates of VAT are applied,  which is the standard rate of 10% and a zero rate of 0%. Goods exported to customers abroad are classified as exempt from VAT or zero rated.'
  //     //     const utter = new SpeechSynthesisUtterance(textAreaContent)
  //     //     utter.lang = 'en-US'
  //     //     utter.pitch = 0.6
  //     //     utter.rate = 0.7
  //     //     utter.text = textAreaContent
  //     //     window.speechSynthesis.speak(utter)
  //     //     utter.addEventListener('end', (event) => {
  //     //       this.readyToListen = false
  //     //     })
  //     //   }
  //     //   else {
  //     //     window.speechSynthesis.cancel()
  //     //   }

  //     // }
  //   }
  // }

  syncTextSizeWithFont() {
    const currentFontSize = getComputedStyle(document.documentElement).fontSize;

    switch (currentFontSize) {
      case '19.2px': // 120% of default 16px
        this.activeBtn = 'zoomIn';
        break;
      case '12.8px': // 80% of default 16px
        this.activeBtn = 'zoomOut';
        break;
      default:
        this.activeBtn = 'normal';
        break;
    }
  }
  enableSpeech() {

    this.readyToListen = true;
    localStorage.setItem('accessibility-listen', 'true');
    resetHeaderSpokenState();
    readEntirePage()
  }

  disableSpeech() {
    this.readyToListen = false;
    localStorage.setItem('accessibility-listen', 'false');

    window.speechSynthesis.cancel();
    console.log("disable speech triggered with readytolisten", this.readyToListen)

  }

  syncThemeStates() {
    // Prefer authoritative values from ThemeService that may be encrypted in storage
    this.darkModeEnabled = this.currentTheme === 'dark';
    this.highContrastEnabled = !!this.isHighContrast;
  }
  // onDarkModeToggle() {
  //   if (this.darkModeEnabled) {
  //     document.body.classList.add('dark-mode');
  //   } else {
  //     document.body.classList.remove('dark-mode');
  //   }
  // }
  // onHighContrastToggle() {
  //   if (this.highContrastEnabled) {
  //     document.body.classList.add('high-contrast');
  //   } else {
  //     document.body.classList.remove('high-contrast');
  //   }
  // }
  onDarkModeToggle() {
    this.darkModeEnabled = !this.darkModeEnabled;
    if (this.darkModeEnabled) {
      this.highContrastEnabled = false;
    }
    this.themeService.toggleLightDark();
    this.setAnnouncementBackgroundSize();
  }

  toggleHighContrast() {
    // this.darkModeEnabled = false;
    // this.highContrastEnabled != this.highContrastEnabled
    this.highContrastEnabled = !this.highContrastEnabled;
    if (this.highContrastEnabled) {
      this.darkModeEnabled = false;
    }
    this.themeService.toggleHighContrast();
    this.setAnnouncementBackgroundSize();
  }


  setAnnouncementBackgroundSize() {
    // if (typeof window !== 'undefined' && window.localStorage) {
    //   const theme = localStorage.getItem('theme');
    //   const highContrast = localStorage.getItem('high-contrast');
    //   const element = document.getElementsByClassName('announcement-div')[0];

    //   if (element) {
    //     const size = theme === 'dark' || highContrast === 'true' ? 'cover' : 'auto 90%';
    //     this.renderer.setStyle(element, 'background-size', size);
    //   }
    // }
  }


  @ViewChild('darkModeSwitch', { read: ElementRef }) element: ElementRef | undefined;

  @ViewChild('highContrastSwitch', { read: ElementRef }) highContrastElement: ElementRef | undefined;



  cross = 'M19.69,5.23L8.96,15.96l-4.23-4.23L2.96,13.5l6,6L21.46,7L19.69,5.23z';
  check = 'M18.3 5.71L12 12l6.3 6.29-1.41 1.41L12 13.41l-6.29 6.3-1.41-1.42L10.59 12 4.29 5.71 5.7 4.29 12 10.59l6.29-6.3z';
  color = 'var(--toggle-slider-color)';
  ngAfterViewInit() {
    if (this.element) {
      this.element.nativeElement.querySelector('.mdc-switch__icon--on').firstChild.setAttribute('d', this.cross);
      this.element.nativeElement.querySelector('.mdc-switch__icon--off').firstChild.setAttribute('d', this.check);
      this.element.nativeElement.querySelector('.mdc-switch__icon--off').firstChild.setAttribute('fill', this.color);
      this.element.nativeElement.querySelector('.mdc-switch__icon--on').firstChild.setAttribute('fill', this.color)
    }
    if (this.highContrastElement) {
      this.highContrastElement.nativeElement.querySelector('.mdc-switch__icon--on').firstChild.setAttribute('d', this.cross);
      this.highContrastElement.nativeElement.querySelector('.mdc-switch__icon--off').firstChild.setAttribute('d', this.check);
      this.highContrastElement.nativeElement.querySelector('.mdc-switch__icon--off').firstChild.setAttribute('fill', this.color);
      this.highContrastElement.nativeElement.querySelector('.mdc-switch__icon--on').firstChild.setAttribute('fill', this.color)
    }
  }



  keyshortCut() {

    let dialogWidth = '400px';

    const screenWidth = window.innerWidth;

    if (screenWidth >= 1440) {
      dialogWidth = '800px';
    } else if (screenWidth >= 1024) {
      dialogWidth = '600px';
    } else if (screenWidth >= 768) {
      dialogWidth = '500px';
    } else if (screenWidth >= 320) {
      dialogWidth = '400px';
    }

    console.info('Opening keyboard shortcuts dialog (width:', dialogWidth, ')');
    import('../../dialog/dialog.component').then(m => {
      this.dialog.open(m.DialogComponent, {
        width: dialogWidth,
        panelClass: 'custom-dialog-container',
        backdropClass: 'custom-dialog-backdrop',
        autoFocus: false,
        disableClose: true,
        scrollStrategy: this.sso.noop(),
        data: {
          page: 'keyboardshortcuts',
          // the JSON file `keyboardshortcuts.json` uses the top-level key `shortcuts`
          table: 'shortcuts'
        }
      });
    }).catch(err => console.error('Failed to open keyboard shortcuts dialog', err));

    this.isShow = !this.isShow;
  }

  closepop() {
    this.isShow = false
  }


  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    const key = (event.key || '').toLowerCase();
    // Only prevent default when we actually handle a shortcut below

    // console.log('Keyboar event');
    // console.log('meta key', event.metaKey);
    // console.log('alt key', event.altKey);
    // console.log('ctrl key', event.ctrlKey);
    // console.log('shift key', event.shiftKey);
    // console.log('key', event.code);



    const isMac = this.deviceInfo && this.deviceInfo.os && this.deviceInfo.os.toLocaleLowerCase() === 'mac';
    // if (isMac) {



    //   if ((event.metaKey && event.altKey && event.code === 'Digit1') || (event.altKey && event.code === 'Digit1')) {
    //     this.darkModeEnabled = !this.darkModeEnabled;
    //     this.onDarkModeToggle();
    //     event.preventDefault();
    //     event.stopPropagation();
    //     return;
    //   }


    //   if ((event.metaKey && event.altKey && event.code === 'Digit2') || (event.altKey && event.code === 'Digit2')) {
    //     this.highContrastEnabled = !this.highContrastEnabled;
    //     this.toggleHighContrast();
    //     event.preventDefault();
    //     event.stopPropagation();
    //     return;
    //   }
    // } else {
    //   if (event.ctrlKey && event.shiftKey && key === 'd') {
    //     this.darkModeEnabled = !this.darkModeEnabled;
    //     this.onDarkModeToggle();
    //     event.preventDefault();
    //     event.stopPropagation();
    //     return;
    //   }

    //   if (event.ctrlKey && event.shiftKey && event.code === 'Digit2') {
    //     this.highContrastEnabled = !this.highContrastEnabled;
    //     this.toggleHighContrast();
    //     event.preventDefault();
    //     event.stopPropagation();
    //     return;
    //   }
    // }
  }
  // @HostListener('window:keydown', ['$event'])
  // handleKeyboardEvent(event: KeyboardEvent) {


  //   // if(event.key.toLocaleLowerCase() === 'tab'){
  //   //     event.preventDefault();
  //   //     const node = document.getSelection()
  //   //     console
  //   // }

  //   if (event.shiftKey && event.key.toLocaleLowerCase() === 'space') {
  //     window.scrollBy({
  //       top: -window.innerHeight,
  //       left: 0,
  //       behavior: 'smooth'
  //     })
  //   } else if (event.key.toLocaleLowerCase() === 'space') {
  //     window.scrollBy({
  //       top: window.innerHeight,
  //       left: 0,
  //       behavior: 'smooth'
  //     })
  //   }

  //   if (event.key.toLocaleLowerCase() === 'arrowright' || event.key.toLocaleLowerCase() === 'end' || (event.altKey && event.key.toLocaleLowerCase() === 'f')) {
  //     window.scrollTo({
  //       top: document.body.scrollHeight,
  //       left: 0,
  //       behavior: 'smooth'
  //     })
  //   } else if (event.key.toLocaleLowerCase() === 'arrowleft' || event.key.toLocaleLowerCase() === 'home' || (event.altKey && event.key.toLocaleLowerCase() === 'g')) {
  //     window.scrollTo({
  //       top: 0,
  //       left: 0,
  //       behavior: 'smooth'
  //     })
  //   }

  //   if ((event.altKey && event.key.toLocaleLowerCase() === 'c')) {
  //     this.router.navigate(['/help/contact-information'])
  //   }
  //   if ((event.altKey && event.key.toLocaleLowerCase() === 't')) {
  //     this.router.navigate(['/terms-of-use'])
  //   }
  //   if ((event.altKey && event.key.toLocaleLowerCase() === 'l')) {
  //     this.router.navigate(['/privacy-policy'])
  //   }
  //   if ((event.altKey && event.key.toLocaleLowerCase() === 'h')) {
  //     this.router.navigate(['/help/call-center'])
  //   }
  //   if ((event.altKey && event.key === '1')) {
  //     this.router.navigate(['/'])
  //   }
  // }

}
